/**
 * AI Coach engine — client-side port of the AI-Personal-Coach Python project's
 * data-driven exercise-detection pipeline (pose_utils.py, validator.py,
 * exercises.py, repetition_counter.py, feedback.py). Rule/threshold data itself
 * lives in aiCoachData.json (generated once via AI-Personal-Coach/export_rules.py
 * from the Python rule registry — see that script for provenance).
 *
 * Rather than porting ~100 bespoke per-exercise Python analyzer functions 1:1,
 * this implements one generic analyzer per rep-counting mode (CYCLE / ALTERNATE /
 * HOLD, read from the exported per-exercise config), parameterized entirely by
 * each exercise's own ported angle-threshold data. Covers all 101 exercises with
 * real rep counting + joint-angle-based form scoring; the bespoke fault callouts
 * (knee_cave / hip_sag / hip_deviation) stay exactly as they were, layered on top.
 */
import RULES from "./aiCoachData.json";

// ─── Name normalization + lookup (port of exercise_rules._normalize_name /
//     repetition_counter._normalize_token — both byte-identical in Python) ───
export function normalizeToken(name) {
  if (!name) return "";
  return name
    .trim()
    .toLowerCase()
    .replace(/-/g, " ")
    .replace(/'/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .join("_");
}

const EXERCISE_KEY_SET = new Set(RULES.exerciseKeys);

export function normalizeExerciseKey(name) {
  const token = normalizeToken(name);
  if (!token) return null;
  if (EXERCISE_KEY_SET.has(token)) return token;
  return RULES.nameAliases[token] || null;
}

export function getExerciseRule(key) {
  return RULES.exerciseRules[key] || null;
}
export function getRepCounterConfig(key) {
  return RULES.repCounterConfigs[key] || null;
}

// ─── Pose math (port of pose_utils.py) ────────────────────────────────────
const LANDMARK_NAMES = [
  "nose", "left_eye_inner", "left_eye", "left_eye_outer",
  "right_eye_inner", "right_eye", "right_eye_outer",
  "left_ear", "right_ear", "mouth_left", "mouth_right",
  "left_shoulder", "right_shoulder", "left_elbow", "right_elbow",
  "left_wrist", "right_wrist", "left_pinky", "right_pinky",
  "left_index", "right_index", "left_thumb", "right_thumb",
  "left_hip", "right_hip", "left_knee", "right_knee",
  "left_ankle", "right_ankle", "left_heel", "right_heel",
  "left_foot_index", "right_foot_index",
];
const INDEX_BY_NAME = Object.fromEntries(LANDMARK_NAMES.map((n, i) => [n, i]));

function pt(lm, name) {
  const p = lm[INDEX_BY_NAME[name]];
  return p ? { x: p.x, y: p.y } : { x: 0, y: 0 };
}
function visOf(lm, name) {
  const p = lm[INDEX_BY_NAME[name]];
  return p && typeof p.visibility === "number" ? p.visibility : 0;
}
function avgVis(lm, names) {
  const vs = names.map((n) => visOf(lm, n));
  return vs.reduce((a, b) => a + b, 0) / vs.length;
}

export function calculateAngle(a, b, c) {
  if (!a || !b || !c) return 0;
  const ab = { x: a.x - b.x, y: a.y - b.y };
  const cb = { x: c.x - b.x, y: c.y - b.y };
  const m = Math.hypot(ab.x, ab.y) * Math.hypot(cb.x, cb.y);
  if (!m) return 0;
  const dot = ab.x * cb.x + ab.y * cb.y;
  return (Math.acos(Math.max(-1, Math.min(1, dot / m))) * 180) / Math.PI;
}
function midpoint(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

const JOINT_ANGLE_DEFS = {
  left_elbow: ["left_shoulder", "left_elbow", "left_wrist"],
  right_elbow: ["right_shoulder", "right_elbow", "right_wrist"],
  left_knee: ["left_hip", "left_knee", "left_ankle"],
  right_knee: ["right_hip", "right_knee", "right_ankle"],
  left_hip: ["left_shoulder", "left_hip", "left_knee"],
  right_hip: ["right_shoulder", "right_hip", "right_knee"],
  left_shoulder: ["left_elbow", "left_shoulder", "left_hip"],
  right_shoulder: ["right_elbow", "right_shoulder", "right_hip"],
};

const LEFT_SIDE = ["left_shoulder", "left_elbow", "left_wrist", "left_hip", "left_knee", "left_ankle"];
const RIGHT_SIDE = ["right_shoulder", "right_elbow", "right_wrist", "right_hip", "right_knee", "right_ankle"];
const MAJOR_JOINTS_FOR_ROM = ["left_knee", "right_knee", "left_elbow", "right_elbow", "left_hip", "right_hip", "left_shoulder", "right_shoulder"];

export function extractPoseMetrics(lm) {
  const jointAngles = {};
  for (const [joint, [a, b, c]] of Object.entries(JOINT_ANGLE_DEFS)) {
    jointAngles[joint] = calculateAngle(pt(lm, a), pt(lm, b), pt(lm, c));
  }

  const shoulderMid = midpoint(pt(lm, "left_shoulder"), pt(lm, "right_shoulder"));
  const hipMid = midpoint(pt(lm, "left_hip"), pt(lm, "right_hip"));
  const kneeMid = midpoint(pt(lm, "left_knee"), pt(lm, "right_knee"));
  const spineAngle = calculateAngle(shoulderMid, hipMid, kneeMid);
  jointAngles.spine = spineAngle;
  jointAngles.neck = calculateAngle(pt(lm, "left_ear"), pt(lm, "left_shoulder"), pt(lm, "left_hip"));
  jointAngles.left_ankle = calculateAngle(pt(lm, "left_knee"), pt(lm, "left_ankle"), pt(lm, "left_foot_index"));
  jointAngles.right_ankle = calculateAngle(pt(lm, "right_knee"), pt(lm, "right_ankle"), pt(lm, "right_foot_index"));

  const leftShoulder = pt(lm, "left_shoulder");
  const rightShoulder = pt(lm, "right_shoulder");
  const shoulderWidth = Math.abs(leftShoulder.x - rightShoulder.x);
  let orientation;
  if (shoulderWidth >= 0.12) {
    orientation = "FRONT";
  } else {
    const leftVis = avgVis(lm, LEFT_SIDE);
    const rightVis = avgVis(lm, RIGHT_SIDE);
    orientation = leftVis === rightVis ? "FRONT" : leftVis > rightVis ? "LEFT" : "RIGHT";
  }

  const romVals = MAJOR_JOINTS_FOR_ROM.map((j) => jointAngles[j]).filter((v) => v != null);
  const rom = romVals.length ? Math.max(...romVals) - Math.min(...romVals) : 0;

  const torsoRef = { x: hipMid.x, y: hipMid.y - 1.0 };
  const torsoAngle = calculateAngle(torsoRef, hipMid, shoulderMid);

  const ankleMid = midpoint(pt(lm, "left_ankle"), pt(lm, "right_ankle"));
  const horizOffset = Math.abs(hipMid.x - ankleMid.x);
  // NOTE: pose_utils.calculate_body_balance already returns 1=balanced/0=unstable;
  // used directly as a 0..1 "goodness" score below (stabilityFrom).
  const balance = Math.max(0, 1 - Math.min(1, horizOffset / 0.15));

  const landmarks = {};
  LANDMARK_NAMES.forEach((name, idx) => {
    const p = lm[idx];
    landmarks[name] = p ? { x: p.x, y: p.y, visibility: p.visibility ?? 0 } : { x: 0, y: 0, visibility: 0 };
  });

  return { jointAngles, landmarks, orientation, rom, torsoAngle, spineAngle, balance };
}

// ─── Angle threshold classification (port of AngleThreshold.classify()) ───
export function classifyAngle(threshold, value) {
  if (!threshold || value == null) return "CRITICAL";
  const deviation = Math.abs(value - threshold.idealAngle);
  if (deviation <= threshold.warningMargin) return "SUCCESS";
  if (deviation <= threshold.criticalMargin) return "WARNING";
  return "CRITICAL";
}

// ─── Validator (port of validator.py's weighted scoring) ──────────────────
const ORIENTATION_MAX = 20, ANGLE_MAX = 50, VIS_MAX = 20, LANDMARKS_MAX = 10;
const VALID_SCORE_THRESHOLD = 60;
const CREDIT = { SUCCESS: 1, WARNING: 0.65, CRITICAL: 0.15 };
const ANGLE_WEIGHT = 0.75, ROM_WEIGHT = 0.25;

function orientationMatches(required, detected) {
  if (!required) return true;
  const req = required.toLowerCase();
  if (req === "any") return true;
  if (!detected) return false;
  const det = detected.toLowerCase();
  if (req === "side") return det === "side" || det === "left" || det === "right";
  return det === req;
}

export function validateExercise(ruleKey, metrics) {
  const rule = getExerciseRule(ruleKey);
  if (!rule || !metrics) {
    return {
      valid: false, score: 0, formAccuracy: 0, matchedOrientation: false, matchedAngles: false,
      details: { incorrectAngles: [], borderlineAngles: [], missingLandmarks: [], orientation: "", visibilityScore: 0, accuracyRatio: 0 },
    };
  }

  const detectedOrientation = (metrics.orientation || "").toLowerCase();
  const matchedOrientation = orientationMatches(rule.requiredOrientation, detectedOrientation);
  const orientationScore = matchedOrientation ? ORIENTATION_MAX : 0;

  const angleDefs = { ...rule.idealFormAngles };
  for (const [joint, threshold] of Object.entries(rule.endPositionAngles || {})) {
    if (!(joint in angleDefs)) angleDefs[joint] = threshold;
  }

  const incorrectAngles = [], borderlineAngles = [], credits = [];
  for (const [joint, threshold] of Object.entries(angleDefs)) {
    const angleValue = metrics.jointAngles[joint];
    if (angleValue == null) { incorrectAngles.push(joint); credits.push(0); continue; }
    const severity = classifyAngle(threshold, angleValue);
    credits.push(CREDIT[severity]);
    if (severity === "WARNING") borderlineAngles.push(joint);
    else if (severity === "CRITICAL") incorrectAngles.push(joint);
  }
  const angleRatio = credits.length ? credits.reduce((a, b) => a + b, 0) / credits.length : 0;
  const romRatio = rule.romThreshold > 0 ? Math.max(0, Math.min(1, (metrics.rom || 0) / rule.romThreshold)) : 1;
  const accuracyRatio = ANGLE_WEIGHT * angleRatio + ROM_WEIGHT * romRatio;
  const angleScore = Math.round(ANGLE_MAX * accuracyRatio);

  const required = rule.requiredLandmarks || [];
  const missingLandmarks = [], visValues = [];
  for (const name of required) {
    const l = metrics.landmarks[name];
    const v = l ? l.visibility : null;
    if (v == null) { missingLandmarks.push(name); continue; }
    visValues.push(v);
    if (v < rule.minimumVisibility) missingLandmarks.push(name);
  }
  const avgVisibility = visValues.length ? visValues.reduce((a, b) => a + b, 0) / visValues.length : 0;
  const presenceRatio = required.length ? (required.length - missingLandmarks.length) / required.length : 1;
  const landmarkScore = Math.round(LANDMARKS_MAX * Math.max(0, presenceRatio));
  const visibilityPoints = Math.round(VIS_MAX * Math.max(0, Math.min(1, avgVisibility)));

  const score = Math.max(0, Math.min(100, orientationScore + angleScore + visibilityPoints + landmarkScore));
  const matchedAngles = accuracyRatio >= VALID_SCORE_THRESHOLD / 100;
  // Pure "did you actually perform the movement's joint angles correctly"
  // percentage — deliberately excludes orientation/visibility/landmark
  // points. Those get you a low `score` for reasons that have nothing to do
  // with rep quality (camera angle, being partly out of frame); gating rep
  // counting on `score` would unfairly block a front-facing user from ever
  // reaching a high accuracy on a "side"-tagged exercise (max ~80/100 with
  // perfect form, since orientationScore is 0 of 20). formAccuracy is what
  // RepetitionCounter's minAccuracyToCount actually checks against.
  const formAccuracy = Math.round(Math.max(0, Math.min(1, accuracyRatio)) * 100);

  let blockingError = false;
  if (required.length && missingLandmarks.length / required.length > 0.5) blockingError = true;

  // NOTE: the Python original hard-blocks validity on orientation mismatch
  // (it assumes a deliberately side-mounted webcam, as instructed by that
  // desktop app). On a phone/laptop, everyone naturally faces the camera
  // head-on, so a "side" requirement would make rep counting silently never
  // work at all for the realistic case, while the skeleton keeps tracking
  // fine — a confusing, broken-looking mismatch. Orientation still costs
  // real points (0 of 20) and still drives the "adjust your position"
  // feedback message, it just no longer hard-blocks counting outright.
  const valid = !blockingError && score >= VALID_SCORE_THRESHOLD;

  return {
    valid, score, formAccuracy, matchedOrientation, matchedAngles,
    details: { incorrectAngles, borderlineAngles, missingLandmarks, orientation: detectedOrientation, visibilityScore: avgVisibility, accuracyRatio },
  };
}

// ─── Generic per-mode analyzer (port of the shared scoring parts of
//     exercises.py; replaces ~100 bespoke _analyze_<exercise>() functions
//     with one template per rep-counting mode, driven by ported thresholds) ─
function stabilityFrom(balance) {
  if (balance == null) return 50;
  return Math.max(0, Math.min(100, balance * 100));
}
function symmetryFrom(left, right) {
  if (left == null || right == null) return 50;
  if (left === 0 && right === 0) return 100;
  const diff = Math.abs(left - right);
  const denom = Math.max(Math.abs(left), Math.abs(right), 1e-6);
  return Math.max(0, Math.min(100, 100 - (diff / denom) * 100));
}
function formScoreFrom({ romScore, stabilityScore, symmetryScore, postureScore, validationScore }) {
  const w = { rom: 0.25, stability: 0.2, symmetry: 0.15, posture: 0.25, validation: 0.15 };
  const total = romScore * w.rom + stabilityScore * w.stability + symmetryScore * w.symmetry + postureScore * w.posture + validationScore * w.validation;
  return Math.max(0, Math.min(100, total));
}
function postureScoreFrom(metrics, incorrectJoints) {
  const inputs = [metrics.spineAngle, metrics.torsoAngle].filter((v) => v != null);
  let score = inputs.length ? inputs.reduce((a, b) => a + b, 0) / inputs.length : 75;
  score = Math.max(0, Math.min(180, score));
  score = Math.max(0, Math.min(100, (score / 180) * 100));
  if (incorrectJoints.length) score = Math.max(0, Math.min(100, score - Math.min(40, 10 * incorrectJoints.length)));
  return score;
}
function confidenceFrom(visibilityScore, validationScore) {
  return Math.round(Math.max(0, Math.min(1, visibilityScore)) * 100 * 0.6 + validationScore * 0.4);
}
function findLeftRightPair(joints) {
  const left = joints.find((j) => j.startsWith("left_"));
  const right = joints.find((j) => j.startsWith("right_"));
  return left && right ? [left, right] : null;
}
function posturePhaseFrom(formScore, isValid) {
  if (!isValid) return "INVALID";
  if (formScore >= 90) return "EXCELLENT";
  if (formScore >= 75) return "GOOD";
  if (formScore >= 55) return "FAIR";
  if (formScore >= 30) return "POOR";
  return "INVALID";
}
function secondaryAngleFrom(rule, metrics, excludeJoints) {
  for (const joint of rule.secondaryJoints || []) {
    if (excludeJoints.includes(joint)) continue;
    const v = metrics.jointAngles[joint];
    if (v != null) return v;
  }
  return null;
}

// Derived joints ("spine"/"neck") aren't real MediaPipe landmarks — their
// visibility is the worst visibility among the landmarks their angle is
// computed from. This matters: MediaPipe still returns *some* x/y estimate
// for an out-of-frame/occluded landmark, just with low visibility — without
// this check, a person only visible from the shoulders up would get noisy,
// physically-meaningless leg/arm angles counted as real rep movement.
const DERIVED_JOINT_LANDMARKS = {
  spine: ["left_shoulder", "right_shoulder", "left_hip", "right_hip", "left_knee", "right_knee"],
  neck: ["left_ear", "left_shoulder", "left_hip"],
};
function jointVisibility(metrics, joint) {
  const names = DERIVED_JOINT_LANDMARKS[joint];
  if (names) return Math.min(...names.map((n) => metrics.landmarks[n]?.visibility ?? 0));
  return metrics.landmarks[joint]?.visibility ?? 0;
}

function analyzeCycle(rule, metrics, validation) {
  const minVis = rule.minimumVisibility || 0.5;
  const jointNames = rule.primaryJoints.length ? rule.primaryJoints : [rule.repCountingJoint];
  const angleEntries = jointNames
    .map((j) => [j, metrics.jointAngles[j], jointVisibility(metrics, j)])
    .filter(([, v, vis]) => v != null && v > 0 && vis >= minVis);
  if (!angleEntries.length) return { state: "INVALID", phase: "UNKNOWN", formScore: 0, completedRom: 0, confidence: 0 };
  const primaryAngle = Math.min(...angleEntries.map(([, v]) => v));

  const startThresh = rule.startPositionAngles[jointNames[0]] || Object.values(rule.startPositionAngles)[0];
  const endThresh = rule.endPositionAngles[jointNames[0]] || Object.values(rule.endPositionAngles)[0];
  if (!startThresh || !endThresh) return { state: "INVALID", phase: "UNKNOWN", formScore: 0, completedRom: 0, confidence: 0 };

  const upThresh = startThresh.idealAngle >= endThresh.idealAngle ? startThresh : endThresh;
  const downThresh = startThresh.idealAngle < endThresh.idealAngle ? startThresh : endThresh;
  const upBound = upThresh.idealAngle - upThresh.warningMargin;
  const downBound = downThresh.idealAngle + downThresh.warningMargin;

  let phase;
  if (primaryAngle <= downBound) phase = "BOTTOM";
  else if (primaryAngle >= upBound) phase = "TOP";
  else phase = "MOVING";

  const span = Math.abs(upBound - downBound) || 1;
  const completedRom = Math.max(0, Math.min(100, (Math.abs(upBound - primaryAngle) / span) * 100));

  const incorrectJoints = validation.details.incorrectAngles;
  const [leftVal, rightVal] = jointNames.length === 2 ? [metrics.jointAngles[jointNames[0]], metrics.jointAngles[jointNames[1]]] : [null, null];
  const symmetryScore = symmetryFrom(leftVal, rightVal);
  const postureScore = postureScoreFrom(metrics, incorrectJoints);
  const stabilityScore = stabilityFrom(metrics.balance);
  const formScore = formScoreFrom({ romScore: completedRom, stabilityScore, symmetryScore, postureScore, validationScore: validation.score });
  const confidence = confidenceFrom(validation.details.visibilityScore, validation.score);

  return {
    state: phase === "MOVING" ? "ACTIVE" : "IDLE",
    phase, formScore: Math.round(formScore * 100) / 100,
    completedRom: Math.round(completedRom * 100) / 100,
    confidence,
    primaryAngle: Math.round(primaryAngle * 10) / 10,
    secondaryAngle: secondaryAngleFrom(rule, metrics, jointNames),
    posture: posturePhaseFrom(formScore, validation.valid),
  };
}

function analyzeAlternate(rule, metrics, validation) {
  const minVis = rule.minimumVisibility || 0.5;
  const pair = findLeftRightPair(rule.primaryJoints) || [rule.repCountingJoint, rule.repCountingJoint];
  const [leftJoint, rightJoint] = pair;
  const leftAngle = metrics.jointAngles[leftJoint];
  const rightAngle = metrics.jointAngles[rightJoint];
  const leftThresh = rule.idealFormAngles[leftJoint];
  const rightThresh = rule.idealFormAngles[rightJoint] || leftThresh;
  const leftVisible = jointVisibility(metrics, leftJoint) >= minVis;
  const rightVisible = jointVisibility(metrics, rightJoint) >= minVis;

  // Requires the tight SUCCESS band specifically (not the wider "not
  // CRITICAL" WARNING tier): ALTERNATE-mode exercises count a rep on every
  // side switch with no return-to-neutral required, so this is the single
  // most noise-sensitive path in the whole engine — a resting/seated pose
  // that happens to sit inside the looser WARNING band (often 2x wider than
  // SUCCESS) was enough to flip LEFT/RIGHT from ordinary posture shifts.
  const leftActive = leftVisible && leftAngle != null && leftThresh && classifyAngle(leftThresh, leftAngle) === "SUCCESS";
  const rightActive = rightVisible && rightAngle != null && rightThresh && classifyAngle(rightThresh, rightAngle) === "SUCCESS";
  if (!leftVisible && !rightVisible) {
    return { state: "INVALID", phase: "UNKNOWN", formScore: 0, completedRom: 0, confidence: 0 };
  }

  let phase = "NEUTRAL";
  if (leftActive && !rightActive) phase = "LEFT";
  else if (rightActive && !leftActive) phase = "RIGHT";
  else if (leftActive && rightActive) {
    const leftDev = Math.abs(leftAngle - leftThresh.idealAngle);
    const rightDev = Math.abs(rightAngle - rightThresh.idealAngle);
    phase = leftDev <= rightDev ? "LEFT" : "RIGHT";
  }

  const incorrectJoints = validation.details.incorrectAngles;
  const postureScore = postureScoreFrom(metrics, incorrectJoints);
  const stabilityScore = stabilityFrom(metrics.balance);
  const completedRom = phase === "NEUTRAL" ? 0 : 100;
  const formScore = formScoreFrom({ romScore: completedRom, stabilityScore, symmetryScore: 75, postureScore, validationScore: validation.score });
  const confidence = confidenceFrom(validation.details.visibilityScore, validation.score);

  return {
    state: phase === "NEUTRAL" ? "IDLE" : "ACTIVE",
    phase, formScore: Math.round(formScore * 100) / 100,
    completedRom, confidence,
    primaryAngle: leftActive ? leftAngle : rightActive ? rightAngle : null,
    secondaryAngle: secondaryAngleFrom(rule, metrics, [leftJoint, rightJoint]),
    posture: posturePhaseFrom(formScore, validation.valid),
  };
}

function analyzeHold(rule, metrics, validation) {
  const minVis = rule.minimumVisibility || 0.5;
  const jointNames = Object.keys(rule.idealFormAngles);
  let holding = jointNames.length > 0;
  for (const joint of jointNames) {
    const value = metrics.jointAngles[joint];
    const threshold = rule.idealFormAngles[joint];
    if (value == null || !threshold || jointVisibility(metrics, joint) < minVis) { holding = false; break; }
    if (classifyAngle(threshold, value) === "CRITICAL") { holding = false; break; }
  }
  const incorrectJoints = validation.details.incorrectAngles;
  const postureScore = postureScoreFrom(metrics, incorrectJoints);
  const stabilityScore = stabilityFrom(metrics.balance);
  const completedRom = holding ? 100 : 0;
  const formScore = formScoreFrom({ romScore: completedRom, stabilityScore, symmetryScore: 75, postureScore, validationScore: validation.score });
  const confidence = confidenceFrom(validation.details.visibilityScore, validation.score);

  return {
    state: holding ? "ACTIVE" : "IDLE",
    phase: holding ? "HOLDING" : "IDLE",
    formScore: Math.round(formScore * 100) / 100,
    completedRom, confidence,
    primaryAngle: jointNames.length ? metrics.jointAngles[jointNames[0]] : null,
    secondaryAngle: secondaryAngleFrom(rule, metrics, jointNames),
    posture: posturePhaseFrom(formScore, validation.valid),
  };
}

export function analyzeExercise(ruleKey, metrics, validation) {
  const rule = getExerciseRule(ruleKey);
  if (!rule) return { state: "INVALID", phase: "UNKNOWN", formScore: 0, completedRom: 0, confidence: 0 };
  // NOTE: deliberately NOT gating on validation.valid here. That score
  // reflects "is your form close to the ideal *bottom* position" (the rule
  // data only defines one target position per joint) — at the natural TOP
  // of any rep that's always going to score low on the angle component, in
  // any orientation, for any exercise. Blocking phase detection whenever
  // that aggregate score dips below its threshold made rep counting fail
  // for real, correctly-performed reps: the skeleton visibly tracked fine,
  // but nothing ever counted. Phase/rep detection is pure geometry and
  // should always run when the relevant joints are visible (each analyzer
  // below already gates on that itself); form quality (validation.score)
  // stays a separate concern surfaced via formScore/posture/feedback only.
  const mode = (getRepCounterConfig(ruleKey) || {}).mode || "CYCLE";
  if (mode === "HOLD") return analyzeHold(rule, metrics, validation);
  if (mode === "ALTERNATE") return analyzeAlternate(rule, metrics, validation);
  return analyzeCycle(rule, metrics, validation);
}

// ─── Repetition counter (port of repetition_counter.py) ───────────────────
// A rep only counts if the user's form during that rep averaged at least
// this much accuracy (validateExercise()'s 0-100 score — joint angles vs.
// each exercise's ported ideal-position thresholds, plus orientation/
// visibility/landmark presence). Requested threshold: "80-85% done
// properly". Geometry-based phase/rep detection (analyzeExercise) stays
// ungated on this — see the note there — this is the one place quality
// actually blocks a rep from being tallied.
export const REP_ACCURACY_THRESHOLD = 80;

export class RepetitionCounter {
  constructor(exerciseKey) {
    this.exerciseKey = null;
    this.config = { mode: "CYCLE", minRomToCount: 55, cooldownSeconds: 0.35, minConfidence: 40, debounceFrames: 2, minAccuracyToCount: REP_ACCURACY_THRESHOLD };
    this._resetState();
    if (exerciseKey) this.initialize(exerciseKey);
  }
  _resetState() {
    this.totalReps = 0;
    this.currentState = "IDLE";
    this.lastState = "IDLE";
    this.reachedExtreme = false;
    this.cycleMaxRom = 0;
    this.lastSide = null;
    this.pendingTag = null;
    this.pendingStreak = 0;
    this.stableTag = null;
    this.lastRepTimestamp = null;
    this.holdStartTs = null;
    this.holdTime = 0;
    this.qualitySum = 0;
    this.qualityCount = 0;
    this.rejectReason = null;
  }
  initialize(exerciseKey) {
    this.exerciseKey = exerciseKey;
    const cfg = getRepCounterConfig(exerciseKey) || this.config;
    // Floors on debounce/cooldown, regardless of the ported config: the
    // original numbers (some as low as 1 debounce frame / 0.15s cooldown,
    // tuned for fast alternating moves like mountain climbers) assume the
    // clean, controlled tracking of the original desktop app. Browser-based
    // MediaPipe over a webcam has more landmark jitter; without a floor,
    // the lowest-debounce exercises counted reps from ordinary noise.
    this.config = {
      ...cfg,
      debounceFrames: Math.max(cfg.debounceFrames ?? 2, 2),
      cooldownSeconds: Math.max(cfg.cooldownSeconds ?? 0.35, 0.25),
      minAccuracyToCount: cfg.minAccuracyToCount ?? REP_ACCURACY_THRESHOLD,
    };
    this._resetState();
  }
  reset() { this._resetState(); }
  getCount() { return this.totalReps; }

  _debounce(tag) {
    if (tag === this.pendingTag) this.pendingStreak += 1;
    else { this.pendingTag = tag; this.pendingStreak = 1; }
    if (this.pendingStreak >= Math.max(1, this.config.debounceFrames)) this.stableTag = tag;
    return this.stableTag != null ? this.stableTag : tag;
  }
  _cooldownElapsed(ts) {
    return this.lastRepTimestamp == null || ts - this.lastRepTimestamp >= this.config.cooldownSeconds;
  }
  _countRep(ts) { this.totalReps += 1; this.lastRepTimestamp = ts; }
  _commit(state) { if (state !== this.currentState) { this.lastState = this.currentState; this.currentState = state; } }

  update(analysisResult, ts, accuracyScore) {
    this.rejectReason = null;
    if (this.exerciseKey == null) return this._response(false, ts, 0);
    const { state, phase, completedRom = 0, confidence = 0 } = analysisResult || {};
    if (state === "INVALID" || confidence < this.config.minConfidence) {
      this._commit("IDLE");
      return this._response(false, ts, confidence);
    }
    let completed = false;
    if (this.config.mode === "HOLD") completed = this._processHold(phase, ts, accuracyScore);
    else if (this.config.mode === "ALTERNATE") completed = this._processAlternate(phase, ts, accuracyScore);
    else completed = this._processCycle(phase, completedRom, ts, accuracyScore);
    return this._response(completed, ts, confidence);
  }

  // Average accuracy is taken only over frames where the user is actually
  // in the movement (BOTTOM/MOVING for cycles, LEFT/RIGHT for alternate) —
  // deliberately excluding idle frames at the top/neutral position between
  // reps, since those don't reflect how well *this* rep was performed and
  // would otherwise drag the average down (or up) for reasons unrelated to
  // the rep just done. Falls back to "pass" (100) when no accuracyScore was
  // supplied at all, so direct RepetitionCounter callers/tests that don't
  // track accuracy keep their existing ROM-only behavior.
  _trackQuality(accuracyScore) {
    if (typeof accuracyScore === "number") {
      this.qualitySum += accuracyScore;
      this.qualityCount += 1;
    }
  }
  _avgQuality() {
    return this.qualityCount ? this.qualitySum / this.qualityCount : 100;
  }
  _resetQuality() {
    this.qualitySum = 0;
    this.qualityCount = 0;
  }

  _processCycle(phase, completedRom, ts, accuracyScore) {
    const raw = phase === "TOP" ? "ANCHOR" : phase === "BOTTOM" ? "BOTTOM" : phase === "MOVING" ? "TRANSITION" : "UNKNOWN";
    const tag = this._debounce(raw);
    // Track quality off the raw, un-debounced phase for *this* frame, not
    // the debounced `tag` — the debouncer can still be reporting the
    // previous stable tag for a frame or two after the raw phase has
    // already moved on (that's what makes it a debouncer). Averaging by
    // `tag` would silently blend a few top-of-rep frames into the
    // bottom-of-rep quality average (or vice versa) purely from that lag.
    if (raw === "BOTTOM" || raw === "TRANSITION") this._trackQuality(accuracyScore);
    let completed = false;
    if (tag === "BOTTOM") {
      this.reachedExtreme = true;
      this.cycleMaxRom = Math.max(this.cycleMaxRom, completedRom);
      this._commit("BOTTOM");
    } else if (tag === "TRANSITION") {
      this._commit(this.reachedExtreme ? "ASCENDING" : "DESCENDING");
    } else if (tag === "ANCHOR") {
      if (this.reachedExtreme && this._cooldownElapsed(ts)) {
        const romOk = this.cycleMaxRom >= this.config.minRomToCount;
        const qualityOk = this._avgQuality() >= this.config.minAccuracyToCount;
        if (romOk && qualityOk) {
          this._countRep(ts);
          completed = true;
        } else {
          this.rejectReason = !romOk ? "low_rom" : "low_accuracy";
        }
        this.reachedExtreme = false;
        this.cycleMaxRom = 0;
        this._resetQuality();
      }
      this._commit(completed ? "TOP" : this.totalReps === 0 ? "START" : "TOP");
    } else {
      this._commit("IDLE");
    }
    return completed;
  }

  _processAlternate(phase, ts, accuracyScore) {
    const raw = phase === "LEFT" ? "LEFT" : phase === "RIGHT" ? "RIGHT" : "NEUTRAL";
    const tag = this._debounce(raw);
    // Same reasoning as _processCycle: key quality tracking off the raw
    // per-frame phase, not the debounced tag, to avoid blending in stale
    // lag frames from the side that just ended.
    if (raw === "LEFT" || raw === "RIGHT") this._trackQuality(accuracyScore);
    let completed = false;
    if (tag === "LEFT" || tag === "RIGHT") {
      if (this.lastSide != null && tag !== this.lastSide && this._cooldownElapsed(ts)) {
        if (this._avgQuality() >= this.config.minAccuracyToCount) {
          this._countRep(ts);
          completed = true;
        } else {
          this.rejectReason = "low_accuracy";
        }
        this._resetQuality();
      }
      this.lastSide = tag;
      this._commit(tag);
    } else {
      this._commit(this.lastSide || "IDLE");
    }
    return completed;
  }

  _processHold(phase, ts, accuracyScore) {
    // A hold only keeps accumulating time while form stays at/above the
    // accuracy bar too — dropping below it (even while the joints are
    // technically still in the non-CRITICAL band analyzeHold checks)
    // pauses the timer instead of silently crediting a sloppy hold.
    const goodQuality = typeof accuracyScore !== "number" || accuracyScore >= this.config.minAccuracyToCount;
    const isHolding = phase === "HOLDING" && goodQuality;
    if (isHolding) {
      if (this.holdStartTs == null) this.holdStartTs = ts;
      this.holdTime = Math.max(0, ts - this.holdStartTs);
      this._commit("HOLDING");
    } else {
      this.holdStartTs = null;
      this.holdTime = 0;
      this._commit("IDLE");
    }
    return false;
  }

  _response(completedRep, ts, confidence) {
    return {
      reps: this.totalReps,
      currentState: this.currentState,
      completedRep,
      // Set only on the single frame a rep attempt was evaluated and
      // rejected — "low_rom" (never reached real depth/extension) or
      // "low_accuracy" (reached it, but average form during the rep was
      // below minAccuracyToCount). Lets the UI say *why* a rep didn't
      // count instead of just staying silent.
      rejectReason: this.rejectReason,
      holdTime: Math.round(this.holdTime * 100) / 100,
      confidence: Math.round(confidence * 100) / 100,
    };
  }
}

// ─── Feedback (port of feedback.py) ────────────────────────────────────────
const JOINT_MESSAGES = {
  back: "Straighten your back.", spine: "Straighten your back.",
  hip: "Lower your hips.", hips: "Lower your hips.",
  elbow: "Extend your elbows.", elbows: "Extend your elbows.",
  knee: "Align your knees.", knees: "Align your knees.",
  shoulder: "Relax your shoulders.", shoulders: "Relax your shoulders.",
  neck: "Keep your neck neutral.",
  wrist: "Keep your wrists steady.", wrists: "Keep your wrists steady.",
  ankle: "Keep your ankles stable.", ankles: "Keep your ankles stable.",
};
function jointMessage(jointName) {
  let key = jointName.trim().toLowerCase().replace(/ /g, "_").replace(/-/g, "_");
  key = key.includes("_") ? key.split("_").pop() : key;
  if (JOINT_MESSAGES[key]) return JOINT_MESSAGES[key];
  for (const [known, msg] of Object.entries(JOINT_MESSAGES)) {
    if (jointName.toLowerCase().includes(known)) return msg;
  }
  return `Adjust your ${jointName.replace(/_/g, " ")}.`;
}

export function generateFeedback(validation, repResult, confidence) {
  if (!validation) return { message: "No feedback available.", level: "INFO" };

  const isUnknown = (validation.errors || []).some((e) => String(e).toLowerCase().includes("unknown exercise"));
  if (isUnknown || (validation.matchedOrientation === false && validation.matchedAngles === false)) {
    return { message: "Selected exercise doesn't match your movement.", level: "ERROR" };
  }

  // A rep attempt just got rejected by RepetitionCounter's accuracy/ROM
  // gate this exact frame — say so explicitly rather than leaving the user
  // to wonder why the counter didn't move.
  if (repResult?.rejectReason === "low_accuracy") {
    return { message: "Rep not counted — form dipped too low. Slow down and aim for full control.", level: "WARNING" };
  }
  if (repResult?.rejectReason === "low_rom") {
    return { message: "Rep not counted — go through the full range of motion.", level: "WARNING" };
  }

  const formScore = validation.score;
  if (formScore < 70) {
    const joint = validation.details?.incorrectAngles?.[0];
    return { message: joint ? jointMessage(joint) : "Correct your posture.", level: "WARNING" };
  }
  if (formScore >= 85) return { message: "Excellent form.", level: "SUCCESS" };
  if (repResult?.completedRep) {
    return { message: `Good Rep - ${Math.round(formScore)}% Form. Keep Refining For Perfect Form.`, level: "SUCCESS" };
  }
  if (confidence != null && confidence < 50) return { message: "Move fully into the camera.", level: "WARNING" };
  return { message: `Good Form (${Math.round(formScore)}%) - Keep Refining.`, level: "SUCCESS" };
}

export function levelToFormBar(level) {
  if (level === "ERROR") return "red";
  if (level === "WARNING") return "amber";
  return "green";
}

export class FeedbackHoldManager {
  constructor(warningHoldSeconds = 3, successHoldSeconds = 2) {
    this.warningHoldSeconds = Math.max(0.1, warningHoldSeconds);
    this.successHoldSeconds = Math.max(0.1, successHoldSeconds);
    this.pinned = null;
    this.pinnedUntil = 0;
  }
  _holdDuration(level) { return level === "WARNING" || level === "ERROR" ? this.warningHoldSeconds : this.successHoldSeconds; }
  update(feedback, nowSeconds) {
    const stillPinned = !!this.pinned && nowSeconds < this.pinnedUntil;
    if (feedback?.message) {
      const isNew = feedback.message !== this.pinned?.message;
      if (!stillPinned || isNew) {
        this.pinned = { ...feedback };
        this.pinnedUntil = nowSeconds + this._holdDuration(feedback.level);
      }
      return this.pinned;
    }
    if (stillPinned) return this.pinned;
    this.pinned = null;
    this.pinnedUntil = 0;
    return null;
  }
  reset() { this.pinned = null; this.pinnedUntil = 0; }
}

// ─── Per-exercise session: bundles rep counting + feedback smoothing ──────
// NOTE: the Python engine has no "partial rep" concept (only completed reps),
// so that JS-only UI hint is intentionally not reproduced here.
export class AICoachSession {
  constructor(exerciseKey) {
    this.exerciseKey = exerciseKey;
    this.repCounter = new RepetitionCounter(exerciseKey);
    this.feedbackHold = new FeedbackHoldManager();
  }
  reset() { this.repCounter.reset(); this.feedbackHold.reset(); }
  processFrame(lm, nowSeconds) {
    const metrics = extractPoseMetrics(lm);
    const validation = validateExercise(this.exerciseKey, metrics);
    const analysis = analyzeExercise(this.exerciseKey, metrics, validation);
    const repResult = this.repCounter.update(analysis, nowSeconds, validation.formAccuracy);
    const liveFeedback = generateFeedback(validation, repResult, analysis.confidence);
    const feedback = this.feedbackHold.update(liveFeedback, nowSeconds) || liveFeedback;
    return { metrics, validation, analysis, repResult, feedback, formBar: levelToFormBar(feedback.level) };
  }
}
